import { createTheme } from '@mui/material/styles';
import { theme as centralEmotionBase } from '../theme';
import { FACILITY_IDS, resolveFacilityId, SP_THEME } from '../utils/facilities';

/** CSS custom properties shared by GlobalStyles + hardcoded UI that opts into vars. */
export const FACILITY_CSS_VARS = {
  [FACILITY_IDS.CENTRAL]: {
    '--al-primary': '#98FB98',
    '--al-primary-dark': '#7BC67B',
    '--al-primary-rgb': '152, 251, 152',
    '--al-bg': '#1C1C1C',
    '--al-surface': '#2C2C2C',
    '--al-surface-2': '#242424',
    '--al-text': '#FFFFFF',
    '--al-text-dim': 'rgba(255, 255, 255, 0.6)',
    '--al-on-primary': '#1C1C1C',
    '--al-border': 'rgba(255, 255, 255, 0.1)',
    '--al-accent': '#98FB98',
    '--al-secondary': '#2196F3',
    '--al-font': "'Montserrat', sans-serif",
    '--al-font-display': "'Montserrat', sans-serif",
    '--al-color-scheme': 'dark'
  },
  [FACILITY_IDS.STRAWBERRY_PATCH]: {
    // Farmstand on dark: Cocoa ground, Harvest accent, Vine panels, Cherry script, Flour type
    '--al-primary': SP_THEME.harvest,
    '--al-primary-dark': '#C49A3E',
    '--al-primary-rgb': '223, 174, 76',
    '--al-bg': SP_THEME.cocoa,
    '--al-surface': '#221E18',
    '--al-surface-2': '#2A251E',
    '--al-text': SP_THEME.flour,
    '--al-text-dim': 'rgba(247, 239, 225, 0.65)',
    '--al-on-primary': SP_THEME.cocoa,
    '--al-border': 'rgba(223, 174, 76, 0.28)',
    '--al-accent': SP_THEME.cherry,
    '--al-secondary': SP_THEME.vine,
    '--al-font': "'Montserrat', sans-serif",
    '--al-font-display': SP_THEME.fonts.caps,
    '--al-color-scheme': 'dark'
  }
};

export const applyFacilityCssVars = (facilityId) => {
  const id = resolveFacilityId(facilityId);
  const vars = FACILITY_CSS_VARS[id] || FACILITY_CSS_VARS[FACILITY_IDS.CENTRAL];
  const root = document.documentElement;
  root.setAttribute('data-facility', id);
  Object.entries(vars).forEach(([key, value]) => {
    root.style.setProperty(key, value);
  });
  root.style.colorScheme = vars['--al-color-scheme'] || 'dark';
  if (document.body) {
    document.body.style.backgroundColor = vars['--al-bg'];
    document.body.style.color = vars['--al-text'];
    document.body.style.fontFamily = vars['--al-font'];
  }
};

export const getEmotionTheme = (facilityId) => {
  const id = resolveFacilityId(facilityId);
  if (id === FACILITY_IDS.STRAWBERRY_PATCH) {
    return {
      ...centralEmotionBase,
      colors: {
        ...centralEmotionBase.colors,
        primary: SP_THEME.harvest,
        primaryDark: '#C49A3E',
        secondary: SP_THEME.vine,
        secondaryDark: '#334635',
        background: SP_THEME.cocoa,
        surface: '#221E18',
        border: 'rgba(223, 174, 76, 0.28)',
        text: SP_THEME.flour,
        textDim: 'rgba(247, 239, 225, 0.65)',
        success: SP_THEME.vine,
        error: SP_THEME.cherry,
        accent: SP_THEME.cherry
      },
      fonts: {
        ...centralEmotionBase.fonts,
        primary: "'Montserrat', sans-serif",
        display: SP_THEME.fonts.caps,
        script: SP_THEME.fonts.script
      }
    };
  }
  return {
    ...centralEmotionBase,
    colors: {
      ...centralEmotionBase.colors,
      accent: centralEmotionBase.colors.primary
    },
    fonts: {
      ...centralEmotionBase.fonts,
      display: centralEmotionBase.fonts.primary,
      script: centralEmotionBase.fonts.primary
    }
  };
};

export const getMuiTheme = (facilityId) => {
  const id = resolveFacilityId(facilityId);
  const isSp = id === FACILITY_IDS.STRAWBERRY_PATCH;
  const emotion = getEmotionTheme(id);
  const primary = isSp ? SP_THEME.harvest : '#98FB98';
  const bg = isSp ? SP_THEME.cocoa : '#1C1C1C';
  const surface = isSp ? '#221E18' : '#2C2C2C';
  const text = isSp ? SP_THEME.flour : '#FFFFFF';
  const border = isSp ? 'rgba(223, 174, 76, 0.28)' : 'rgba(255, 255, 255, 0.1)';
  const hover = isSp ? 'rgba(223, 174, 76, 0.08)' : 'rgba(152, 251, 152, 0.05)';
  const secondary = isSp ? SP_THEME.vine : '#2196F3';

  return createTheme({
    // Keep AL emotion-shaped tokens on the MUI theme so @emotion/styled
    // (which often receives the MUI theme context) can still read colors/spacing.
    colors: emotion.colors,
    fonts: emotion.fonts,
    fontWeights: emotion.fontWeights,
    alTypography: emotion.typography,
    alSpacing: emotion.spacing,
    alBreakpoints: emotion.breakpoints,
    palette: {
      mode: 'dark',
      primary: { main: primary },
      secondary: { main: secondary },
      error: { main: isSp ? SP_THEME.cherry : '#F44336' },
      background: {
        default: isSp ? SP_THEME.cocoa : '#000000',
        paper: surface
      },
      text: {
        primary: text,
        secondary: isSp ? 'rgba(247, 239, 225, 0.7)' : 'rgba(255, 255, 255, 0.7)'
      }
    },
    spacing: (factor) => `${8 * factor}px`,
    breakpoints: {
      values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 }
    },
    typography: {
      fontFamily: "'Montserrat', sans-serif",
      h6: {
        color: primary,
        fontWeight: 600,
        ...(isSp ? { fontFamily: SP_THEME.fonts.caps, letterSpacing: '0.04em' } : {})
      },
      button: { textTransform: 'uppercase' },
      h1: {
        fontSize: '24px',
        fontWeight: 600,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
        ...(isSp ? { fontFamily: SP_THEME.fonts.caps } : {})
      },
      body1: {
        fontSize: '14px',
        fontWeight: 400,
        letterSpacing: '0.02em'
      }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: bg,
            color: text
          }
        }
      },
      MuiTableContainer: {
        styleOverrides: {
          root: {
            backgroundColor: surface,
            boxShadow: 'none',
            border: `1px solid ${border}`,
            marginTop: '16px'
          }
        }
      },
      MuiTable: {
        styleOverrides: {
          root: { backgroundColor: surface }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: { backgroundColor: bg }
        }
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: border,
            padding: '8px 16px',
            fontSize: '0.85rem',
            textTransform: 'uppercase',
            color: text
          },
          head: {
            fontWeight: 600,
            backgroundColor: bg,
            color: text
          }
        }
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            '&:hover': { backgroundColor: hover },
            '&:last-child td': { borderBottom: 0 },
            '&.completed td': {
              textDecoration: 'line-through',
              color: isSp ? 'rgba(247, 239, 225, 0.45)' : 'rgba(255, 255, 255, 0.5)'
            }
          }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundColor: surface,
            borderRadius: 0
          }
        }
      },
      MuiButton: {
        styleOverrides: {
          containedPrimary: {
            color: isSp ? SP_THEME.cocoa : '#1C1C1C',
            fontWeight: 600
          }
        }
      }
    }
  });
};
